/**
 * Geo-blocking transport layer.
 *
 * This module only *fetches* the viewer's country/region. Every policy
 * decision — which countries are sanctioned, which US states are blocked —
 * belongs to `@/config/geo`, the single source of truth, which is mirrored at
 * the Vercel Edge by the redirect rules in the root `vercel.json`.
 *
 * Do not reintroduce country or state lists here. Earlier copies drifted out
 * of sync with the policy (they blocked Hawaii, omitted Louisiana and
 * Pennsylvania, and blocked Syria and Venezuela, none of which matched
 * QSAVE_GEOFENCING_KYC_POLICY.md).
 */

import { isSanctioned, isTokenInteractionBlocked } from '@/config/geo';

// Environment check for test mode
const isTestMode = () => {
  return import.meta.env.MODE === 'development' ||
         import.meta.env.MODE === 'test' ||
         import.meta.env.VITE_ENABLE_TEST_GEO === 'true';
};

// Get country and state from various sources (for testing and production)
export const getLocationInfo = async (): Promise<{ countryCode: string | null; stateCode?: string | null }> => {
  // Check for test override first (ONLY in test/dev mode)
  if (typeof window !== 'undefined' && isTestMode()) {
    // Method 1: Query parameter override for testing
    const params = new URLSearchParams(window.location.search);
    const testCountry = params.get('test_country');
    const testState = params.get('test_state');
    if (testCountry) {
      console.warn(`[GEO-TEST] Using test country: ${testCountry}${testState ? `, state: ${testState}` : ''}`);
      return { countryCode: testCountry.toUpperCase(), stateCode: testState?.toUpperCase() || null };
    }

    // Method 2: Header override (requires server support)
    const testHeader = document.querySelector('meta[name="x-test-country"]');
    const testStateHeader = document.querySelector('meta[name="x-test-state"]');
    if (testHeader) {
      const country = testHeader.getAttribute('content');
      const state = testStateHeader?.getAttribute('content');
      if (country) {
        console.warn(`[GEO-TEST] Using test country from header: ${country}${state ? `, state: ${state}` : ''}`);
        return { countryCode: country.toUpperCase(), stateCode: state?.toUpperCase() || null };
      }
    }
  }

  try {
    // Production: Use actual geo-location API
    let url = '/api/geo-check';

    // In test mode, append test_country and test_state to API call
    if (isTestMode() && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const testCountry = params.get('test_country');
      const testState = params.get('test_state');
      if (testCountry) {
        url += `?test_country=${testCountry}`;
        if (testState) {
          url += `&test_state=${testState}`;
        }
      }
    }

    const response = await fetch(url);
    const data = await response.json();
    return {
      countryCode: data.countryCode,
      stateCode: data.stateCode || null
    };
  } catch (error) {
    console.error('Failed to get location info:', error);
    return { countryCode: null, stateCode: null };
  }
};

// Main geo-check function. Policy is delegated to `@/config/geo`.
export const checkGeoRestrictions = async (): Promise<{
  allowed: boolean;
  countryCode: string | null;
  stateCode?: string | null;
  isTest?: boolean;
}> => {
  const { countryCode, stateCode } = await getLocationInfo();

  if (!countryCode) {
    // If we can't determine country, allow access
    return { allowed: true, countryCode: null, stateCode: null };
  }

  // `allowed` reflects the site-wide sanctions block. Token-surface gating is
  // path-dependent and is applied by the caller (see DomainRouter).
  const restricted = isSanctioned(countryCode, stateCode);

  const isTest = isTestMode() && typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).has('test_country') ||
     !!document.querySelector('meta[name="x-test-country"]'));

  return {
    allowed: !restricted,
    countryCode,
    stateCode,
    isTest
  };
};

// Re-exported for callers that need the token-surface predicate alongside the
// sanctions predicate without importing two modules.
export { isSanctioned, isTokenInteractionBlocked };

// Backwards compatibility - keep getCountryCode function for existing code
export const getCountryCode = async (): Promise<string | null> => {
  const { countryCode } = await getLocationInfo();
  return countryCode;
};
